import React from 'react';
import { Sparkles, BookOpen, GraduationCap, Eye } from 'lucide-react';
import { cn } from '../lib/utils';
import { inferirPerfilRubrica } from './PerfilRubricaSelector';

/**
 * Mapeo de id de tarea -> id de módulo + título amigable.
 */
const TAREA_A_MODULO = {
    'tarea-1': { modulo: 'modulo-1', titulo: 'Planificación' },
    'tarea-2': { modulo: 'modulo-1', titulo: 'Planificación' },
    'tarea-3': { modulo: 'modulo-1', titulo: 'Planificación' },
    'tarea-4': { modulo: 'modulo-2', titulo: 'Clase Grabada' },
    'tarea-5': { modulo: 'modulo-2', titulo: 'Clase Grabada' },
    'tarea-6': { modulo: 'modulo-2', titulo: 'Clase Grabada' },
    'tarea-m3-5': { modulo: 'modulo-3', titulo: 'Trabajo Colaborativo' },
    // Aliases legacy (M3 antes usaba tarea-7/8/9)
    'tarea-7': { modulo: 'modulo-3', titulo: 'Trabajo Colaborativo' },
    'tarea-8': { modulo: 'modulo-3', titulo: 'Trabajo Colaborativo' },
    'tarea-9': { modulo: 'modulo-3', titulo: 'Trabajo Colaborativo' },
};

const PERFIL_LABEL = {
    'Generalista': 'Generalista',
    'Básica': 'Educación Básica',
    'Media': 'Educación Media',
};

const PERFIL_TONO = {
    'Generalista': {
        icon: Sparkles,
        bg: 'bg-emerald-50',
        ring: 'ring-emerald-100',
        text: 'text-emerald-800',
        dot: 'bg-emerald-500',
    },
    'Básica': {
        icon: BookOpen,
        bg: 'bg-sky-50',
        ring: 'ring-sky-100',
        text: 'text-sky-800',
        dot: 'bg-sky-500',
    },
    'Media': {
        icon: GraduationCap,
        bg: 'bg-violet-50',
        ring: 'ring-violet-100',
        text: 'text-violet-800',
        dot: 'bg-violet-500',
    },
};

const NEUTRAL_TONO = {
    icon: Eye,
    bg: 'bg-slate-50',
    ring: 'ring-slate-100',
    text: 'text-slate-700',
    dot: 'bg-slate-400',
};

/**
 * Resuelve el perfil de rúbrica activo a partir del perfil del docente.
 * Prioriza override manual > inferencia por nivel.
 */
export function resolverPerfilActivo(perfil) {
    if (!perfil) return null;
    return perfil.perfil_rubrica || inferirPerfilRubrica(perfil.nivel) || null;
}

/**
 * Resuelve módulo + número de indicador a partir de un indicador id.
 * indicador id puede ser "indicador-1-1" (M1), "indicador-m2-9" (M2), "indicador-m3-16" (M3).
 */
function extraerNumIndicador(indicadorId) {
    if (!indicadorId) return null;
    const match = indicadorId.match(/(\d+)$/);
    return match ? match[1] : null;
}

/**
 * Mini-insignia elegante que comunica al/la docente el contexto pedagógico activo:
 * perfil de rúbrica + módulo + (opcional) número de indicador.
 *
 * Ejemplos:
 *   - "Mirada: Educación Media · Clase Grabada · Indicador 9"
 *   - "Perfil: Generalista · Planificación"
 *   - "Contexto: Educación Básica · Clase Grabada"
 *
 * Props:
 *   - perfil: objeto perfil del usuario (con `perfil_rubrica` opcional + `nivel`)
 *   - tareaId: id de la tarea actual (opcional)
 *   - indicadorId: id del indicador actual (opcional)
 *   - indicadorNombre: nombre del indicador (opcional, fallback si no hay numérico)
 *   - variante: "compact" | "full" — compact omite el sufijo "Mirada:"
 *   - className
 */
export default function ContextoPedagogicoBadge({
    perfil,
    tareaId,
    indicadorId,
    indicadorNombre,
    variante = 'full',
    className,
}) {
    const perfilActivo = resolverPerfilActivo(perfil);
    const moduloInfo = TAREA_A_MODULO[tareaId];
    const numIndicador = extraerNumIndicador(indicadorId);

    // Si no hay nada que comunicar, no renderizar
    if (!perfilActivo && !moduloInfo) return null;

    const tono = perfilActivo ? PERFIL_TONO[perfilActivo] : NEUTRAL_TONO;
    const Icon = tono.icon;
    const labelPerfil = perfilActivo ? PERFIL_LABEL[perfilActivo] : null;

    const piezas = [];
    if (labelPerfil) piezas.push(labelPerfil);
    if (moduloInfo) piezas.push(moduloInfo.titulo);
    if (numIndicador) {
        piezas.push(`Indicador ${numIndicador}`);
    } else if (indicadorNombre && !numIndicador) {
        // Cortar el nombre largo del indicador para que no rompa el layout
        const corto = indicadorNombre.length > 36 ? `${indicadorNombre.slice(0, 33)}…` : indicadorNombre;
        piezas.push(corto);
    }

    const prefijo = variante === 'compact' ? '' : 'Mirada: ';

    return (
        <div
            className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1.5 ring-1 transition-all duration-300',
                tono.bg,
                tono.ring,
                className
            )}
            data-testid="contexto-pedagogico-badge"
            data-perfil={perfilActivo || 'sin-perfil'}
            data-modulo={moduloInfo?.modulo || ''}
            title={`Contexto pedagógico desde el que la IA te acompaña`}
        >
            <span className={cn('relative flex h-2 w-2 shrink-0 items-center justify-center')}>
                <span className={cn('absolute h-full w-full animate-ping rounded-full opacity-40', tono.dot)} />
                <span className={cn('relative h-2 w-2 rounded-full', tono.dot)} />
            </span>
            <Icon className={cn('h-3.5 w-3.5 shrink-0', tono.text)} />
            <span className={cn('text-xs font-medium leading-none', tono.text)}>
                {prefijo}
                {piezas.map((p, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <span className="mx-1.5 opacity-40">·</span>}
                        <span className={i === 0 ? 'font-semibold' : 'opacity-90'}>{p}</span>
                    </React.Fragment>
                ))}
            </span>
        </div>
    );
}
